import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, BarChart3, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import NewsFeed from "../components/NewsFeed";
import TradeDialog from "../components/TradeDialog";
import CanSlimCard from "../components/CanSlimCard";
import NewsMoodRibbon from "../components/NewsMoodRibbon";
import WhyNowCallout from "../components/WhyNowCallout";
import AlertDialog from "../components/AlertDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import AssetHeader from "../components/asset/AssetHeader";
import AssetCharts from "../components/asset/AssetCharts";
import AssetSidebar from "../components/asset/AssetSidebar";

/**
 * Asset Detail page — chart, indicators, news, fundamentals, AI insight,
 * sentiment, CAN SLIM (stocks only) and watchlist controls.
 *
 * Thin orchestrator: owns the quote+watchlist data fetches and the tab/range
 * /chart-mode state. Header, chart panel and sidebar live in
 * `components/asset/*`. Was 423 lines as one file; now ~150.
 */
export default function AssetDetail() {
  const { type, symbol } = useParams();
  const { user } = useAuth();
  const [range, setRange] = useState("1M");
  const [chartMode, setChartMode] = useState("area");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lists, setLists] = useState([]);
  const [watchIds, setWatchIds] = useState({}); // watchlist_id -> item_id
  const [watchLoading, setWatchLoading] = useState(false);
  const [tradeOpen, setTradeOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.get(`/quote/${type}/${symbol}?range=${range}`)
      .then((res) => { if (!cancel) setData(res.data); })
      .catch((e) => {
        if (!cancel) {
          toast.error(e.response?.data?.detail || "Failed to load data");
          setData(null);
        }
      })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [type, symbol, range]);

  useEffect(() => {
    if (!user) return;
    api.get("/watchlists").then(async (r) => {
      const ls = r.data.watchlists || [];
      setLists(ls);
      const map = {};
      for (const l of ls) {
        const items = (await api.get(`/watchlist?watchlist_id=${l.id}`)).data.items || [];
        const found = items.find((i) => i.symbol === symbol.toUpperCase() && i.asset_type === type);
        if (found) map[l.id] = found.id;
      }
      setWatchIds(map);
    }).catch((e) => console.warn("watchlist load failed:", e));
  }, [user, symbol, type]);

  const toggleInList = useCallback(async (listId) => {
    if (!user) { toast.info("Sign in to save to watchlist"); return; }
    setWatchLoading(true);
    try {
      if (watchIds[listId]) {
        await api.delete(`/watchlist/${watchIds[listId]}`);
        const copy = { ...watchIds }; delete copy[listId]; setWatchIds(copy);
        toast.success("Removed from list");
      } else {
        const r = await api.post("/watchlist", {
          symbol: data.symbol, name: data.name, asset_type: type, watchlist_id: listId,
        });
        setWatchIds({ ...watchIds, [listId]: r.data.id });
        toast.success("Added");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setWatchLoading(false);
    }
  }, [user, watchIds, data, type]);

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-8 md:py-12" data-testid="asset-detail-page">
        <Link
          to={user ? "/dashboard" : "/"}
          className="inline-flex items-center gap-1.5 text-sm text-[#6B7068] hover:text-[#2A2F2B] mb-6 transition-soft"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back
        </Link>

        {loading && !data && (
          <div className="flex items-center gap-2 text-[#6B7068]">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading {symbol}...
          </div>
        )}

        {data && (
          <>
            <AssetHeader
              data={data}
              type={type}
              range={range}
              user={user}
              lists={lists}
              watchIds={watchIds}
              watchLoading={watchLoading}
              onOpenTrade={() => setTradeOpen(true)}
              onOpenAlert={() => setAlertOpen(true)}
              onToggleList={toggleInList}
            />

            <div className="mb-3">
              <NewsMoodRibbon assetType={type} symbol={data.symbol} />
            </div>
            <div className="mb-5">
              <WhyNowCallout assetType={type} symbol={data.symbol} />
            </div>

            <Tabs defaultValue="chart" className="w-full">
              <TabsList className="bg-white border border-[#EAE6E1] rounded-full p-1 h-auto mb-6" data-testid="asset-tabs">
                <TabsTrigger value="chart" data-testid="tab-chart" className="rounded-full px-5 py-1.5 text-sm data-[state=active]:bg-[#2A2F2B] data-[state=active]:text-white">
                  <BarChart3 className="w-4 h-4 mr-1.5 inline" strokeWidth={1.5} /> Chart
                </TabsTrigger>
                {type === "stock" && (
                  <TabsTrigger value="canslim" data-testid="tab-canslim" className="rounded-full px-5 py-1.5 text-sm data-[state=active]:bg-[#2A2F2B] data-[state=active]:text-white">
                    CAN SLIM
                  </TabsTrigger>
                )}
                <TabsTrigger value="news" data-testid="tab-news" className="rounded-full px-5 py-1.5 text-sm data-[state=active]:bg-[#2A2F2B] data-[state=active]:text-white">
                  News
                </TabsTrigger>
              </TabsList>

              <TabsContent value="chart">
                <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
                  <div className="lg:col-span-2 space-y-6">
                    <AssetCharts
                      data={data}
                      type={type}
                      range={range}
                      setRange={setRange}
                      chartMode={chartMode}
                      setChartMode={setChartMode}
                    />
                  </div>
                  <AssetSidebar data={data} type={type} range={range} />
                </div>
              </TabsContent>

              <TabsContent value="news">
                <NewsFeed symbol={data.symbol} asset_type={type} />
              </TabsContent>

              {type === "stock" && (
                <TabsContent value="canslim">
                  <CanSlimCard symbol={data.symbol} variant="detailed" />
                </TabsContent>
              )}
            </Tabs>

            <TradeDialog
              open={tradeOpen}
              onOpenChange={setTradeOpen}
              asset={{ symbol: data.symbol, name: data.name, asset_type: type }}
              currentPrice={data.price}
              onTraded={() => {}}
            />

            <AlertDialog
              open={alertOpen}
              onOpenChange={setAlertOpen}
              symbol={data.symbol}
              name={data.name}
              assetType={type}
              currentPrice={data.price}
            />
          </>
        )}
      </main>
    </div>
  );
}
